import { format, isAfter, isBefore, parseISO } from "date-fns";
import React from "react";

import { Avatar } from "@performyard/py-components";

import TableChip from "../../../../../../components/Chip/templates/TableChip";
// components
import Link from "../../../../../../components/Link";
import Typography from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";

import { Review } from "../../../../../../api/services/searchServiceResultTypes/reviews";

// utils
import toEmployeeUrl from "../../../../../../utils/URLs/Employee";

// css
import css from "../AllReviews.module.scss";

const getAllReviewsTableCols = () => {
	return [
		{
			id: "name",
			label: "Name",
			isSortable: true,
			width: 365,
			isSticky: true,
			getter: (review: Review) => {
				const { name, _id, awaiting_approval_count } = review;
				const count360 = review["360_count"] || 0;
				let maxTextWidth = 350;
				if (count360) {
					maxTextWidth -= 95;
				}
				if (awaiting_approval_count) {
					maxTextWidth -= 125;
				}

				return (
					<div className={css.nameColumn}>
						<Link href={`/view-review/${_id}/`}>
							<TruncatedText
								maxWidth={maxTextWidth}
								size="paragraph-3"
								color="primary-5"
								weight="regular"
								text={name}
							/>
						</Link>
						<div className={css.chipGroup}>
							{count360 ? <TableChip color="blue" label="360 Enabled" /> : null}
							{awaiting_approval_count ? <TableChip color="green" label="Awaiting Approval" /> : null}
						</div>
					</div>
				);
			},
		},
		{
			id: "bulk_review.name",
			label: "Review Cycle",
			isSortable: true,
			width: 200,
			getter: (review: Review) => {
				const { bulk_review } = review;
				return (
					<TruncatedText maxWidth={195} size="paragraph-3" weight="regular" text={bulk_review?.name || ""} />
				);
			},
		},
		{
			id: "subject_name",
			label: "Subject",
			isSortable: true,
			width: 159,
			getter: (review: Review) => {
				const subjectID = review?.subject || "";
				const subjectName = review?.subject_name || "";

				return (
					<div className="flex items-center gap-2">
						<Avatar
							size={20}
							dangerouslySetClassName={css.userAvatar}
							id={subjectID}
							alt={`${subjectName}'s avatar`}
						/>
						<div className={css.userName}>
							<Link href={toEmployeeUrl(subjectID)} forceHardReload={true}>
								<TruncatedText
									useTooltip="never"
									maxWidth={125}
									size="paragraph-3"
									color="primary-5"
									weight="regular"
									text={subjectName}
								/>
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			id: "start_date",
			label: "Start Date",
			isSortable: true,
			width: 148,
			getter: (review: Review) => {
				const { start_date } = review;

				const parsedDate = parseISO(start_date);

				return (
					<Typography size="paragraph-3" weight="regular">
						{format(parsedDate, "MMM dd, yyyy h:mm a")}
					</Typography>
				);
			},
		},
		{
			id: "status",
			label: "Status",
			isSortable: false,
			width: 160,
			getter: (review: Review) => {
				const { start_date, completed, unassigned_report_form_count } = review;
				const parsedStartDate = parseISO(start_date);
				const currentDate = new Date();

				let status = "";
				if (completed) {
					status = "Completed";
				} else if (isBefore(parsedStartDate, currentDate)) {
					status = "Active";
				} else if (isAfter(parsedStartDate, currentDate)) {
					status = `Starting ${format(parsedStartDate, "MMM dd, yyyy")}`;
				}
				const unassigned = unassigned_report_form_count > 0 ? "(unassigned forms)" : "";
				const renderedText = `${status} ${unassigned}`;

				return <TruncatedText maxWidth={155} size="paragraph-3" weight="regular" text={renderedText} />;
			},
		},
	];
};

export default getAllReviewsTableCols;
